import { HttpErrorResponse } from '@angular/common/http';
import { TimeoutError } from 'rxjs';
import { ApiEnvelope } from './api-envelope';
import { Language, fallbackLanguageOf } from '@core/i18n/domain/language';
import { flattenTranslations, isI18nKey, TranslationMap } from '@core/i18n/domain/translation.types';
import { LOCAL_TRANSLATIONS } from '@core/i18n/data/translations.dictionary';

/**
 * ApiError — the single normalised error every /v2 backend call rejects with.
 *
 * `message` is read FROM THE API (the envelope/Laravel `message`, already
 * localized by `Accept-Language`); when the API gives none, it falls back to a
 * current/default message per HTTP status. `fieldErrors` carries Laravel 422
 * `{ errors }` for per-field display.
 */
export class ApiError extends Error {
  constructor(
    readonly httpStatus: number,
    override readonly message: string,
    readonly fieldErrors: Record<string, string[]> | null = null,
    readonly raw: unknown = null,
    /**
     * TRUE when `message` was read FROM THE API body (envelope/Laravel `message`),
     * FALSE when it is our own per-status/network default. Lets a caller decide
     * whether to surface the API's own message or its own suitable fallback copy —
     * see {@link apiErrorMessage}.
     */
    readonly fromApi: boolean = false,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Built-in fallback copy. The HTTP layer is root-scoped and cannot reach the
 * route-scoped i18n dictionary, so these defaults live here (AR + EN) and are
 * only used when the API returns NO message (rare — the backend localizes its
 * own messages via Accept-Language).
 */
const DEFAULT_ERR: Record<'ar' | 'en', Record<string, string>> = {
  ar: {
    network: 'تعذّر الاتصال بالخادم، يرجى التحقّق من اتصالك والمحاولة مرة أخرى.',
    timeout: 'حدث خطأ، يرجى المحاولة مرة أخرى.',
    unauthorized: 'انتهت صلاحية الجلسة، يرجى تسجيل الدخول من جديد.',
    forbidden: 'لا تملك صلاحية للقيام بهذا الإجراء.',
    notFound: 'العنصر المطلوب غير موجود.',
    rateLimited: 'محاولات كثيرة، يرجى الانتظار قليلاً ثم المحاولة مرة أخرى.',
    server: 'حدث خطأ، يرجى المحاولة مرة أخرى.',
    generic: 'حدث خطأ، يرجى المحاولة مرة أخرى.',
  },
  en: {
    network: 'Could not reach the server. Please check your connection and try again.',
    timeout: 'An error occurred, please try again.',
    unauthorized: 'Your session has expired. Please sign in again.',
    forbidden: 'You do not have permission for this action.',
    notFound: 'The requested item was not found.',
    rateLimited: 'Too many attempts. Please wait a moment and try again.',
    server: 'An error occurred, please try again.',
    generic: 'An error occurred, please try again.',
  },
};

function defaultKeyFor(status: number): string {
  if (!status) return 'network';
  if (status === 401) return 'unauthorized';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'notFound';
  if (status === 429) return 'rateLimited';
  if (status >= 500) return 'server';
  return 'generic';
}

/** The localized fallback copy for a given dictionary key. */
function copyFor(key: string, lang: string): string {
  const table = lang === Language.En ? DEFAULT_ERR.en : DEFAULT_ERR.ar;
  return table[key] ?? table['generic']!;
}

/** The "current"/default message when the API supplies none. */
export function defaultErrorMessage(status: number, lang: string): string {
  return copyFor(defaultKeyFor(status), lang);
}

/**
 * Some endpoints return a raw i18n KEY as the error `message` (snake_case, e.g.
 * `reference_portal_care_cr9_share_failed`) instead of already-localized prose.
 * We resolve such a key against the BUNDLED dictionary so the user sees readable
 * text. The HTTP layer is root-scoped and cannot reach the route-scoped,
 * API-merged dictionary, so it reads the bundled LOCAL copy; app error keys
 * always live there.
 *
 * `LOCAL_TRANSLATIONS` is now backed by a lazy per-language chunk (see
 * `translations.dictionary.ts`) rather than an eager combined dictionary —
 * this function stays synchronous regardless, because every REAL call site
 * (`api-error.spec.ts` documents this) passes the app's own currently-active
 * language (`lang.current()` — see auth.facade.ts / http.service.ts /
 * api-error-alerts.service.ts), which `main.ts`/`main.server.ts`/
 * `TranslationFacade.initialize()` already guarantee is preloaded before any
 * HTTP call can happen. The one narrow, already-documented gap is
 * `displayMessage()`'s FALLBACK-language lookup below, which can still miss if
 * that OTHER language's chunk hasn't loaded yet (mirrors
 * `TranslationFacade.loadFallback()`'s own accepted trade-off) — memoizing
 * only a non-empty flatten (not `??=`) is what keeps that case self-healing:
 * a still-loading language flattens to `{}` and is NOT cached, so the next
 * call re-checks rather than being stuck on an empty result forever.
 */
const flatLocal: Partial<Record<Language, TranslationMap>> = {};
function localDictionary(lang: Language): TranslationMap {
  const cached = flatLocal[lang];
  if (cached) return cached;
  const flattened = flattenTranslations(LOCAL_TRANSLATIONS[lang] ?? {});
  if (Object.keys(flattened).length > 0) flatLocal[lang] = flattened;
  return flattened;
}

/**
 * Turn an API `message` into display-ready text. Already-localized prose passes
 * through untouched; a raw i18n KEY is resolved via {@link localDictionary}
 * (active language, then the fallback language), and when the key resolves to
 * nothing we show the static per-status default copy IN ITS PLACE rather than a
 * raw, unreadable key. So: read the message-key correctly when we can, else a
 * static readable message. `ApiError.message` is thus always display-ready —
 * both the toast and the inline state-view read it directly.
 */
function displayMessage(message: string, status: number, lang: string): string {
  const l: Language = lang === Language.En ? Language.En : Language.Ar;
  if (!isI18nKey(message)) return message; // already-localized prose
  const hit = localDictionary(l)[message] ?? localDictionary(fallbackLanguageOf(l))[message];
  return hit && hit.trim() ? hit : defaultErrorMessage(status, l);
}

/** rxjs `timeout()` rejects with a `TimeoutError`, not an `HttpErrorResponse`. */
function isTimeoutError(err: unknown): boolean {
  return err instanceof TimeoutError || (err as { name?: string } | null)?.name === 'TimeoutError';
}

/**
 * Trim a value down to a non-empty string. A `string[]` (e.g. a Laravel field
 * bag `["The email is required."]` or a `message: [...]`) is unwrapped to its
 * first usable entry. Anything else (object, number, null, blank) → null.
 */
function asMessage(v: unknown): string | null {
  if (typeof v === 'string') return v.trim() || null;
  if (Array.isArray(v)) {
    for (const item of v) {
      const s = asMessage(item);
      if (s) return s;
    }
  }
  return null;
}

/**
 * Pull a message out of an `errors` value, tolerant of every shape:
 *   · `"..."` / `["..."]`                       → the string
 *   · `{ message: "..." }`                       → the message
 *   · Laravel `{ field: ["msg", ...], ... }`     → the first field's first msg
 *   · `[{ message: "..." }, ...]`                → the first item's message
 */
function messageFromErrors(errors: unknown): string | null {
  const direct = asMessage(errors);
  if (direct) return direct;
  if (errors && typeof errors === 'object') {
    const obj = errors as Record<string, unknown>;
    const own = asMessage(obj['message']);
    if (own) return own;
    for (const val of Object.values(obj)) {
      const s = asMessage(val) ?? asMessage((val as { message?: unknown } | null)?.message);
      if (s) return s;
    }
  }
  return null;
}

/**
 * Safely read a human-readable message out of an arbitrary error body, tolerant
 * of every shape the backend (or a proxy/CDN/load-balancer) might send. Tries,
 * in priority order, each access fully guarded:
 *   1. `body` is itself a plain string  (text/plain responses)
 *   2. `body.message`                   (envelope / Laravel `{ message }`)
 *   3. `body.errors[.message]`          (Laravel 422 field bag, or `{ errors: { message } }`)
 *   4. `body.error` (string) / `body.error.message` / `body.error.errors`  (nested error object)
 * Returns null when nothing usable is found, so the caller falls back to the
 * localized static copy in {@link defaultErrorMessage}.
 */
function messageFromBody(body: unknown): string | null {
  // The whole body is a plain string.
  const whole = asMessage(body);
  if (whole) return whole;

  if (!body || typeof body !== 'object') return null;
  const b = body as Record<string, unknown>;

  // `{ message }`
  const top = asMessage(b['message']);
  if (top) return top;

  // `{ errors: ... }`
  const fromErrors = messageFromErrors(b['errors']);
  if (fromErrors) return fromErrors;

  // `{ error: ... }` — a string, or a nested `{ error: { message | errors } }`.
  const inner = b['error'];
  const innerStr = asMessage(inner);
  if (innerStr) return innerStr;
  if (inner && typeof inner === 'object') {
    const io = inner as Record<string, unknown>;
    const im = asMessage(io['message']);
    if (im) return im;
    const ie = messageFromErrors(io['errors']);
    if (ie) return ie;
  }

  return null;
}

function fieldErrorsFromBody(body: unknown): Record<string, string[]> | null {
  if (body && typeof body === 'object') {
    const e = (body as { errors?: unknown }).errors;
    if (e && typeof e === 'object') return e as Record<string, string[]>;
  }
  return null;
}

/** A business error returned as HTTP 200 + `{ status:false, message }`. */
export function businessError<T>(env: ApiEnvelope<T>, lang: string): ApiError {
  const apiMsg = messageFromBody(env);
  return new ApiError(
    200,
    apiMsg ? displayMessage(apiMsg, 200, lang) : defaultErrorMessage(200, lang),
    fieldErrorsFromBody(env),
    env,
    !!apiMsg,
  );
}

/** A transport error (timeout/4xx/5xx/0-network) → normalised with the API message or a default. */
export function toApiError(err: unknown, lang: string): ApiError {
  if (err instanceof ApiError) return err;

  // A client-side timeout (request took too long) — surface the friendly "try again" copy.
  if (isTimeoutError(err)) {
    return new ApiError(0, copyFor('timeout', lang), null, err);
  }

  if (err instanceof HttpErrorResponse) {
    const status = err.status ?? 0;
    // Show whatever message the API body gives (any status) — resolving it first
    // when the backend sent a raw i18n key; fall back to static copy when the
    // body has none (e.g. a network failure with no usable body).
    const apiMsg = messageFromBody(err.error);
    const message = apiMsg ? displayMessage(apiMsg, status, lang) : defaultErrorMessage(status, lang);
    return new ApiError(status, message, fieldErrorsFromBody(err.error), err, !!apiMsg);
  }
  return new ApiError(0, defaultErrorMessage(0, lang), null, err);
}

/**
 * The backend-supplied, display-ready `response.message` for this error — or
 * `null` when the API sent none (or only a technical 5xx/network body we never
 * surface). Callers show this when present, otherwise their OWN suitable,
 * translated per-request fallback copy. Follows {@link toApiError}'s message
 * rules exactly: a raw i18n key is resolved to prose; 5xx/network/timeout → null.
 */
export function apiErrorMessage(err: unknown, lang: string): string | null {
  if (err instanceof ApiError) return err.fromApi ? err.message : null;
  if (isTimeoutError(err)) return null;
  if (err instanceof HttpErrorResponse) {
    const status = err.status ?? 0;
    const raw = messageFromBody(err.error);
    return raw ? displayMessage(raw, status, lang) : null;
  }
  return null;
}
